/**
 * Where money can be sent.
 *
 * Shared by fans and creators, because a bank account is a bank account and
 * splitting this in two would be two places for the same mistake.
 *
 * The two rails are presented differently on purpose, and the difference is not
 * cosmetic — it is the security model showing through:
 *
 *   A **gift card** takes an email address, typed here. It is not a credential,
 *   and asking someone to go through a hosted flow to supply one would be
 *   theatre.
 *
 *   A **bank account** takes nothing at all. Pressing the button asks the
 *   server for a reference, the processor's own flow collects the numbers, and
 *   the account appears in this list when the processor confirms it. There is
 *   no field here for an account number, and there is deliberately no way to
 *   add one — see migration 0027.
 */
import { useState } from 'react';
import {
  useAddGiftCardDestination,
  useBeginBankLink,
  usePayoutDestinations,
  usePayoutMethods,
  useRemovePayoutDestination,
} from '../api/hooks.js';
import { ErrorNote, Panel } from './shared.js';
import type { PayoutDestinationWire, PayoutMethodInfoWire } from '../api/client.js';

export function DestinationsPanel() {
  const destinations = usePayoutDestinations(true);
  const methods = usePayoutMethods(true);
  const addGiftCard = useAddGiftCardDestination();
  const beginBankLink = useBeginBankLink();
  const remove = useRemovePayoutDestination();

  const [email, setEmail] = useState('');
  const [awaitingBank, setAwaitingBank] = useState(false);

  const rows = destinations.data ?? [];

  return (
    <Panel title="Where your money goes" subtitle="Add once, then cashing out is one tap.">
      {destinations.isError && <ErrorNote error={destinations.error} />}
      {addGiftCard.isError && <ErrorNote error={addGiftCard.error} />}
      {beginBankLink.isError && <ErrorNote error={beginBankLink.error} />}
      {remove.isError && <ErrorNote error={remove.error} />}

      {destinations.isPending && <p className="empty">Loading…</p>}

      {!destinations.isPending && rows.length === 0 && (
        <p className="empty">Nothing yet. Add an email for gift cards, or link a bank account.</p>
      )}

      {rows.length > 0 && (
        <ul className="cards">
          {rows.map((row) => (
            <li key={row.id} className="card-row">
              <span className="card-row__digits">{row.displayName}</span>
              <span className="card-row__expiry">{describe(row, methods.data)}</span>
              <button
                className="button button--quiet"
                onClick={() => remove.mutate(row.id)}
                disabled={remove.isPending}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = email.trim();
          if (trimmed.length === 0) return;
          addGiftCard.mutate(trimmed, { onSuccess: () => setEmail('') });
        }}
      >
        <label className="field">
          <span className="field__label">Email for gift cards</span>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
          <span className="field__hint">
            The card is emailed here, so use an address you can open.
          </span>
        </label>
        <button className="button button--quiet" type="submit" disabled={addGiftCard.isPending}>
          {addGiftCard.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>

      <div className="pending-note">
        <strong>Bank account</strong>
        <span>
          {/* Said plainly, because it is the reassuring part and it is true.
              There is no field on this page that takes an account number, and
              the server has nowhere to put one. */}
          Your account details go straight to our payment processor — they never reach us. We only
          ever see a name like “Chase •••• 4305”.
        </span>
        <div className="pending-note__actions">
          <button
            className="button"
            onClick={() =>
              beginBankLink.mutate(undefined, { onSuccess: () => setAwaitingBank(true) })
            }
            disabled={beginBankLink.isPending}
          >
            {beginBankLink.isPending ? 'Starting…' : 'Link a bank account'}
          </button>
        </div>
      </div>

      {/*
        The same shape as card enrolment, and for the same reason: the browser
        genuinely cannot know the outcome. The processor confirms the link over
        a webhook, so the account appears on its own — a spinner here would
        imply we are waiting on ourselves.
      */}
      {awaitingBank && (
        <div className="pending-note">
          <strong>Waiting for your bank to confirm.</strong>
          <span>Your account will appear above on its own — you can leave this page.</span>
        </div>
      )}
    </Panel>
  );
}

/**
 * What to expect from this destination, in the server's words.
 *
 * A confirmed bank account says so. A typed email does not claim to be
 * verified, because it is not — and a badge that says "verified" next to
 * something nobody checked is worse than no badge.
 */
function describe(
  destination: PayoutDestinationWire,
  methods: PayoutMethodInfoWire[] | undefined,
): string {
  const timing = methods?.find((entry) => entry.method === destination.method)?.timing;
  const verified = destination.verifiedBy === 'provider_confirmed' ? 'Confirmed by your bank' : null;
  return [verified, timing].filter((part) => part !== null).join(' · ');
}
